import React, { useCallback, useMemo } from "react";
import useOperationMapping from "../masters/useOperationMapping";
import OperationMappingFields from "../masters/OperationMappingFields";
import {
  useAddMachineMutation,
  useDeleteMachineMutation,
  useGetMachinesQuery,
  useUpdateMachineMutation,
} from "../../features/machines/machineApi";
import { usePlantSelection } from "../../features/plants/PlantSelectionContext";
import { useLazyGetLinesQuery } from "../../features/lines/lineApi";
import { useLazyPlantOptions } from "../../features/plants/usePlantOptions";
import useRelationTypeField from "../../features/relationTypes/useRelationTypeField";
import MasterCrudPage from "../masters/MasterCrudPage";
import { withAuditFilters } from "../masters/filterMetadata";
import {
  getPlantLabel,
  getLineLabel,
  renderPlantCell,
} from "../masters/masterMetadata";

const getMachineQrValue = (row) => row?.qr_value || row?.machine_id;
const getMachineQrDisplayValue = (row) => row?.qr_display_text || row?.machine_id;
const machineFilterFields = withAuditFilters([
  { name: "machine_id", label: "Machine ID", optionSource: "machines" },
  { name: "machine_name", label: "Machine Name" },
  { name: "machine_code", label: "Machine Code" },
  { name: "relation_type", label: "Relation Type" },
  { name: "line_id", label: "Line ID", optionSource: "lines" },
  { name: "machine_position", label: "Machine Position", type: "number", min: 1, step: 1 },
]);

const Machine = () => {
  const { selectedPlantId } = usePlantSelection();
  const { field: relationTypeField, loadOptions: loadRelationTypes } = useRelationTypeField();
  const mapping = useOperationMapping("machine", selectedPlantId);
  const {
    options: plantOptions,
    isFetching: isLoadingPlants,
    loadOptions: loadPlantOptions,
  } = useLazyPlantOptions();
  const [loadLines, { data: lineData, isFetching: isLoadingLines }] =
    useLazyGetLinesQuery();

  const lineOptionsByPlant = useMemo(() => {
    const optionsByPlant = new Map();

    (lineData?.data || []).forEach((line) => {
      if (!line?.plant_id || !line?.line_id) return;

      const plantId = String(line.plant_id);
      const currentOptions = optionsByPlant.get(plantId) || [];
      currentOptions.push({
        value: line.line_id,
        label: line.line_name
          ? `${line.line_name}${line.line_code ? ` (${line.line_code})` : ""} (${line.line_id})`
          : String(line.line_id),
      });
      optionsByPlant.set(plantId, currentOptions);
    });

    return optionsByPlant;
  }, [lineData?.data]);

  const getLineOptions = useCallback(
    (formData, editData) => {
      const plantId = formData.plant_id || editData?.plant_id;
      if (!plantId) return [];

      const options = lineOptionsByPlant.get(String(plantId)) || [];
      if (!editData?.line_id || options.some((option) => option.value === editData.line_id)) {
        return options;
      }

      return [
        ...options,
        {
          value: editData.line_id,
          label: String(editData.line_id),
        },
      ];
    },
    [lineOptionsByPlant]
  );

  const loadFormOptions = useCallback(() => {
    loadPlantOptions();
    loadRelationTypes();
    if (selectedPlantId) {
      loadLines({ page: 1, limit: 1000, plant_id: selectedPlantId });
    }
  }, [selectedPlantId, loadLines, loadPlantOptions, loadRelationTypes]);

  const fields = useMemo(
    () => [
      {
        name: "plant_id",
        label: "Plant",
        type: "select",
        options: plantOptions,
        placeholder: isLoadingPlants ? "Loading plants..." : "Select plant",
        disabled: isLoadingPlants && plantOptions.length === 0,
        disabledOnEdit: true,
        required: true,
        fullWidth: true,
        resetFields: ["line_id"],
      },
      {
        name: "line_id",
        label: "Line",
        type: "select",
        options: getLineOptions,
        placeholder: (formData) => {
          if (!formData.plant_id) return "Select plant first";
          if (isLoadingLines) return "Loading lines...";
          return "Select line";
        },
        disabled: (formData) =>
          !formData.plant_id ||
          (isLoadingLines && getLineOptions(formData).length === 0),
        required: true,
        fullWidth: true,
      },
      {
        name: "machine_name",
        label: "Machine Name",
        placeholder: "Enter machine name",
        required: true,
      },
      {
        name: "machine_code",
        label: "Machine Code",
        placeholder: "Enter machine code",
        required: true,
      },
      {
        name: "machine_position",
        label: "Machine Position",
        type: "number",
        min: 1,
        step: 1,
        placeholder: "Enter position",
        required: true,
      },
      { ...relationTypeField, required: true, onChange: mapping.changeRelationType },
    ],
    [getLineOptions, isLoadingLines, isLoadingPlants, plantOptions, relationTypeField, mapping.changeRelationType]
  );

  const columns = useMemo(
    () => (metaData) => [
      {
        name: "Machine ID",
        selector: (row) => row?.machine_id || "-",
        sortable: true,
      },
      {
        name: "Machine Name",
        selector: (row) => row?.machine_name || "-",
        sortable: true,
        grow: 2,
      },
      {
        name: "Machine Code",
        selector: (row) => row?.machine_code || "-",
        sortable: true,
      },
      {
        name: "Relation Type",
        selector: (row) => row?.relation_type || "-",
        sortable: true,
      },
      {
        name: "Plant",
        selector: (row) => getPlantLabel(row, metaData),
        cell: (row) => renderPlantCell(row, metaData),
        sortable: true,
        grow: 2,
      },
      {
        name: "Line",
        selector: (row) => getLineLabel(row, metaData),
        sortable: true,
        grow: 2,
      },
      {
        name: "Position",
        selector: (row) => row?.machine_position ?? "-",
        sortable: true,
      },
    ],
    []
  );

  return (
    <MasterCrudPage
      entityName="Machine"
      pageTitle="Machines"
      idField="machine_id"
      displayField="machine_name"
      fields={fields}
      columns={columns}
      searchFields={[
        "machine_id",
        "machine_name",
        "machine_code",
        "relation_type",
        "plant_id",
        "line_id",
        "machine_position",
        (row, metaData) => getPlantLabel(row, metaData),
        (row, metaData) => getLineLabel(row, metaData),
      ]}
      searchPlaceholder="Search machines"
      useListQuery={useGetMachinesQuery}
      useCreateMutation={useAddMachineMutation}
      useUpdateMutation={useUpdateMachineMutation}
      useDeleteMutation={useDeleteMachineMutation}
      filterFields={machineFilterFields}
      qrDisplayValue={getMachineQrDisplayValue}
      qrValue={getMachineQrValue}
      transformPayload={(payload, editData) => {
        if (!editData) return payload;
        const updatePayload = { ...payload };
        delete updatePayload.plant_id;
        return updatePayload;
      }}
      onOpenForm={(row) => {
        loadFormOptions();
        mapping.load(row);
      }}
      onAfterSave={mapping.save}
      formDisabled={mapping.isLoading || Boolean(mapping.error) || mapping.hasInvalidOperations || mapping.value.some((id) => !id)}
      extraFormContent={(isSaving) => <OperationMappingFields mapping={mapping} disabled={isSaving} />}
      noDataLabel="No machines found"
    />
  );
};

export default Machine;
